/**
 * 2.206.2. On a phone, a word too wide for its line is set smaller until it
 * fits; the line's other words wrap as they did.
 *
 * Kelly: "Regarding mobile, can the font auto size to fit the phone so no one
 * word is too wide? The rest wrap?"
 *
 * Where the page's phone rules apply -- 48rem wide or narrower, by the window
 * or, in the editor's Tablet and Mobile views, by the canvas -- each run of
 * text whose widest word is wider than its room is given a smaller size, just
 * small enough for that word. Text whose words fit is not touched, and nothing
 * is touched on a wider screen.
 *
 * Measured rather than estimated. How wide a word is depends on its font,
 * weight, letter-spacing and capitals, and the browser has laid every one of
 * them out already; a count of characters would be right for one font and
 * wrong for the next.
 *
 * ## The room
 *
 * The width a line has when no word in it is holding it open. A long word
 * does not only run out of its line: in a column that takes its width from its
 * words -- the hero's -- it holds the whole column open past the screen, and
 * every line in that column goes with it. That is why ENTERTAINMENT also
 * pushed the line under it off the left edge.
 *
 * So the room is read with `overflow-wrap: anywhere`, which stops any word
 * holding anything open, and `max-width: none`, which sets aside a hero line's
 * Wrap Size: that is in characters, so it narrows as the type does and could
 * never be caught up with. Both go on every line at once and come off again
 * before anything is drawn.
 *
 * Widths are fitted first, and only then -- in a line drawn narrower than its
 * room -- where the words sit. While a word holds its column open, the
 * column's other lines are out of place without being too wide, and they keep
 * their size.
 *
 * ## What is changed
 *
 * The element that sets the text's size -- the line itself, or a span in it
 * with a size of its own -- is given an inline `font-size` in pixels. Every run
 * first puts back what was there, so turning a phone on its side, or the
 * editor back to Desktop, shows the size that was chosen.
 *
 * In the editor, text inside a field typed into on the canvas is fitted only
 * when its size is the field's own. The canvas saves a field's markup as it
 * is typed, and a size fitted inside it would be saved as if chosen.
 */

/** The width, in rem, below which the page's phone rules apply: editor.css's
 *  `@media (max-width: 48rem)` and its `@container` twin. */
export const PHONE_WIDTH_REM = 48;

/** How close is close enough, in pixels. Layout is in fractions of a pixel. */
export const TOLERANCE = 0.5;

/** Each pass brings the words in by the share they are over. A line whose
 *  start moves as its type shrinks (a centered line narrower than its column)
 *  takes a few passes to settle. */
export const MAX_PASSES = 12;

/** Text that is not drawn as lines of words. */
const SKIP = "script,style,noscript,template,select,option,textarea,svg,math";

/** Blocks, outermost: the published page and every preview give each one. */
const BLOCK = "[data-block-id]";

export interface Extent {
  left: number;
  right: number;
}

/**
 * The share of its size that makes the widest of `words` as wide as `room`,
 * or 1 when none is wider. Each piece of a word on a line of its own counts
 * alone: a word the browser has already broken (at a hyphen) is two pieces.
 */
export function widthFactor(room: Extent, words: Extent[], tolerance = TOLERANCE): number {
  // No words drawn -- a closed panel, a hidden menu -- is nothing too wide.
  if (words.length === 0) return 1;
  let widest = 0;
  for (const word of words) widest = Math.max(widest, word.right - word.left);
  const width = room.right - room.left;
  return widest > width + tolerance ? width / widest : 1;
}

/**
 * The share of its size that brings every one of `words` inside `room`, or 1
 * when they are inside it already.
 *
 * The span from the leftmost of the words or the room's left edge, whichever
 * is further left, to the rightmost of them or the room's right edge, against
 * the room. Asked only of a line drawn narrower than its room -- a hero line's
 * Wrap Size, at the center or the right: a word longer than the line runs
 * out of the line from where the line starts, and so can run out of the room
 * while no wider than it. As the type shrinks the line narrows and its start
 * moves, so this is asked again until the words are in.
 */
export function placeFactor(room: Extent, words: Extent[], tolerance = TOLERANCE): number {
  let left = Infinity;
  let right = -Infinity;
  for (const word of words) {
    left = Math.min(left, word.left);
    right = Math.max(right, word.right);
  }
  if (left >= room.left - tolerance && right <= room.right + tolerance) return 1;
  return (room.right - room.left) / (Math.max(right, room.right) - Math.min(left, room.left));
}

/** Whether lines may break in text with this computed style. A line that may
 *  not is somebody's choice -- a menu item, a name cut short with an
 *  ellipsis -- and its width is looked after elsewhere. */
export function mayWrap(style: CSSStyleDeclaration): boolean {
  const mode = style.getPropertyValue("text-wrap-mode");
  if (mode) return mode !== "nowrap";
  return style.whiteSpace !== "nowrap" && style.whiteSpace !== "pre";
}

/** The ranges of the words in a text node: what lies between the spaces a
 *  line may break at. A no-break space joins, as it does on the page. */
export function wordRanges(node: Text): Range[] {
  const ranges: Range[] = [];
  const pattern = /[^ \t\n\r\f]+/g;
  for (let match = pattern.exec(node.data); match; match = pattern.exec(node.data)) {
    const range = node.ownerDocument.createRange();
    range.setStart(node, match.index);
    range.setEnd(node, match.index + match[0].length);
    ranges.push(range);
  }
  return ranges;
}

/** Whether a block is drawn by the page's phone rules: the window is 48rem or
 *  narrower, or the size container it sits in is (the editor's Tablet and
 *  Mobile canvases; the console's preview frame). The same two questions
 *  editor.css asks with `@media` and `@container`. */
export function isPhone(block: Element): boolean {
  const view = block.ownerDocument.defaultView!;
  if (view.matchMedia(`(max-width: ${PHONE_WIDTH_REM}rem)`).matches) return true;
  for (let at = block.parentElement; at; at = at.parentElement) {
    const style = view.getComputedStyle(at);
    if (style.containerType && style.containerType !== "normal") {
      const width = at.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const rem = parseFloat(view.getComputedStyle(block.ownerDocument.documentElement).fontSize);
      return width <= PHONE_WIDTH_REM * rem;
    }
  }
  return false;
}

/** One run of text: everything whose size one element sets, in one line box. */
interface Run {
  owner: HTMLElement;
  line: HTMLElement;
  words: Range[];
}

/** One property of an element's inline style, as it was. */
interface Kept {
  element: HTMLElement;
  property: string;
  value: string;
  priority: string;
  hadStyle: boolean;
}

function keep(element: HTMLElement, property: string): Kept {
  return {
    element,
    property,
    value: element.style.getPropertyValue(property),
    priority: element.style.getPropertyPriority(property),
    hadStyle: element.hasAttribute("style"),
  };
}

function putBack(kept: Kept): void {
  const { element, property, value, priority, hadStyle } = kept;
  if (value) element.style.setProperty(property, value, priority);
  else element.style.removeProperty(property);
  // An element that had no style attribute has none again, rather than an
  // empty one.
  if (!hadStyle && !element.getAttribute("style")) element.removeAttribute("style");
}

/** The sizes this file has set, to put back before the next run. */
let fitted: Kept[] = [];

/** The element whose lines the text sits in: the nearest that is not inline. */
function lineOf(element: HTMLElement, view: Window): HTMLElement {
  let at: HTMLElement = element;
  while (at.parentElement) {
    const display = view.getComputedStyle(at).display;
    if (display !== "inline" && display !== "contents") break;
    at = at.parentElement;
  }
  return at;
}

/** The element that sets the text's size: the first from the text's own
 *  element up to its line whose size is not simply its parent's. */
function ownerOf(element: HTMLElement, line: HTMLElement, view: Window): HTMLElement {
  for (let at: HTMLElement = element; at !== line && at.parentElement; at = at.parentElement) {
    if (view.getComputedStyle(at).fontSize !== view.getComputedStyle(at.parentElement).fontSize) {
      return at;
    }
  }
  return line;
}

function contentExtent(element: HTMLElement, view: Window): Extent {
  const box = element.getBoundingClientRect();
  const style = view.getComputedStyle(element);
  return {
    left: box.left + parseFloat(style.borderLeftWidth) + parseFloat(style.paddingLeft),
    right: box.right - parseFloat(style.borderRightWidth) - parseFloat(style.paddingRight),
  };
}

function extentsOf(words: Range[]): Extent[] {
  const extents: Extent[] = [];
  for (const word of words) {
    for (const rect of Array.from(word.getClientRects())) {
      if (rect.width > 0) extents.push({ left: rect.left, right: rect.right });
    }
  }
  return extents;
}

/** Every run of text in the blocks the phone rules draw. */
function runsIn(doc: Document): Run[] {
  const view = doc.defaultView!;
  const runs = new Map<HTMLElement, Run>();
  const blocks = Array.from(doc.querySelectorAll<HTMLElement>(BLOCK))
    .filter((block) => !block.parentElement?.closest(BLOCK));
  for (const block of blocks) {
    if (!isPhone(block)) continue;
    const walker = doc.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
      const parent = node.parentElement;
      if (!parent || !/\S/.test(node.data) || parent.closest(SKIP)) continue;
      if (!mayWrap(view.getComputedStyle(parent))) continue;
      const line = lineOf(parent, view);
      const owner = ownerOf(parent, line, view);
      const field = parent.closest<HTMLElement>('[contenteditable="true"]');
      if (field && field !== owner && field.contains(owner)) continue;
      const run = runs.get(owner) ?? { owner, line, words: [] };
      run.words.push(...wordRanges(node));
      runs.set(owner, run);
    }
  }
  return Array.from(runs.values()).filter((run) => run.words.length > 0);
}

/** Shrink each run by `factor` until it says 1, or the passes run out. */
function settle(
  runs: Run[],
  rooms: Map<HTMLElement, Extent>,
  view: Window,
  factor: (room: Extent, words: Extent[]) => number,
): void {
  let over = runs;
  for (let pass = 0; pass < MAX_PASSES && over.length > 0; pass += 1) {
    // Read everything, then write everything: a size written between two
    // reads makes the browser lay the page out again for the second.
    const sizes: [Run, number][] = [];
    for (const run of over) {
      const share = factor(rooms.get(run.line)!, extentsOf(run.words));
      if (share < 1) sizes.push([run, parseFloat(view.getComputedStyle(run.owner).fontSize) * share]);
    }
    for (const [run, size] of sizes) {
      if (!fitted.some((kept) => kept.element === run.owner)) fitted.push(keep(run.owner, "font-size"));
      run.owner.style.setProperty("font-size", `${size}px`, "important");
    }
    over = sizes.map(([run]) => run);
  }
}

/**
 * Fit every word in the document's phone-drawn blocks to its room. Returns
 * how many elements were given a size.
 */
export function fitWords(doc: Document = document): number {
  const view = doc.defaultView!;
  for (const kept of fitted.reverse()) putBack(kept);
  fitted = [];

  const runs = runsIn(doc);
  if (runs.length === 0) return 0;

  // The rooms, with nothing holding a line open: on every line at once, since
  // two long lines in one column each hold it open for the other.
  // Put back to the letter: nothing else runs in between.
  const lines = Array.from(new Set(runs.map((run) => run.line)));
  const held = lines.map((line) => [line, line.getAttribute("style")] as const);
  for (const line of lines) {
    line.style.setProperty("overflow-wrap", "anywhere", "important");
    line.style.setProperty("max-width", "none", "important");
  }
  const rooms = new Map(lines.map((line) => [line, contentExtent(line, view)]));
  for (const [line, style] of held) {
    if (style === null) line.removeAttribute("style");
    else line.setAttribute("style", style);
  }

  // The widths first. Until every word is no wider than its room, a column
  // one of them holds open moves every other line in it, and their words are
  // out of place without being too wide -- they are not to be made smaller.
  settle(runs, rooms, view, widthFactor);
  // Then where they sit, in a line drawn narrower than its room. Not in any
  // other: the room is read from the page drawn a little differently, and a
  // line as wide as its room -- a button sized to its words, centered -- can
  // sit a pixel to one side of where the room was read with every word in it.
  const narrow = runs.filter((run) => {
    const room = rooms.get(run.line)!;
    const line = contentExtent(run.line, view);
    return line.right - line.left < room.right - room.left - TOLERANCE;
  });
  settle(narrow, rooms, view, placeFactor);
  return fitted.length;
}

/**
 * Fit now, and again whenever the answer could change: the window or the
 * editor's canvas changing width, a web font arriving, blocks being drawn or
 * redrawn (the editor draws its canvas after this file runs, and again on every
 * edit), or words being typed on the canvas.
 */
function begin(): void {
  let queued = false;
  const again = (): void => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      fitWords(document);
      watchWidths();
    });
  };

  // Widths only: a fitted size changes a block's height, and answering that
  // would fit again for nothing.
  const widths = new WeakMap<Element, number>();
  const resized = new ResizeObserver((entries) => {
    let changed = false;
    for (const entry of entries) {
      const before = widths.get(entry.target);
      const width = entry.contentRect.width;
      widths.set(entry.target, width);
      if (before !== undefined && before !== width) changed = true;
    }
    if (changed) again();
  });
  const watchWidths = (): void => {
    document.querySelectorAll(BLOCK).forEach((block) => {
      if (!block.parentElement?.closest(BLOCK)) resized.observe(block);
    });
  };

  fitWords(document);
  watchWidths();
  window.addEventListener("resize", again, { passive: true });
  void document.fonts.ready.then(again);
  document.fonts.addEventListener("loadingdone", again);
  new MutationObserver(again).observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });
}

// Not under the DOM tests, which import this before they have a document.
if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", begin);
  } else {
    begin();
  }
}
